import { ReactNode, RefCallback, useCallback, useEffect, useState } from "react"
import { useIdle, useMount, useUnmount, useBeforeUnload, useToggle, useTimeoutFn } from "react-use"
import cn from "classnames"

import { Text } from "ui-kit/components-branded/index"
import { OWNER, VIDEO_SIZE } from "modules/Call/common/constants"
import { useHandleError, useInitSdk, useVideoCall } from "modules/Call/common/hooks"
import {
    ConnectionStatus,
    ChangeVideoToggle,
    InCallBanner,
    RoomPageFrame,
    VideoControls,
    VideoUserNameWithPopover,
    VoxMediaPlayer,
} from "modules/Call/components/index"
import { getRootNode } from "utils/getRootNode"
import { Spinner } from "components/Spinner"
import { useMediaIsMobile } from "utils/helpers"
import { VoximplantSdk } from "modules/Call/sdk"
import { PageType } from "modules/Call/common/constants/pageType"
import { useInputDevicesContext } from "modules/Call/InputDevices"

const doNotCloseBannerAppearBeforeEndTime = 10 * 60 * 1000

const layoutClass = "justify-content-center"
const idleClass = "is-body-touched"

interface ParticipantData {
    name: string
    renderAvatar(useSmallAndBigSize: boolean, isHidden?: boolean): ReactNode
}

interface Props {
    voximplantUserName: string
    outbound: ParticipantData
    inbound?: ParticipantData
    bookingHash: string
    owner: OWNER
    hangUpLink: string
    lobbyLink: string
    generateTokenApiUrl: string
    sessionStartDateTime?: string
    sessionDuration?: number
}

function calcSessionRemainingTime(sessionStartDateTime?: string, sessionDuration?: number) {
    if (!sessionStartDateTime || !sessionDuration) return undefined
    let msAfterStart
    try {
        msAfterStart = Date.now() - new Date(sessionStartDateTime).getTime()
    } catch {
        return undefined
    }
    return sessionDuration * 60 * 1000 - msAfterStart
}

function calculateLayoutFlags({
    endpointsCount,
    screenShareEnabled,
    isPsychotherapyMode,
    isMobile,
    sizeVideo,
    doNotCloseBannerEnabled,
}: {
    endpointsCount: number
    screenShareEnabled: boolean
    isPsychotherapyMode: boolean
    isMobile: boolean
    sizeVideo: VIDEO_SIZE
    doNotCloseBannerEnabled: boolean
}) {
    const isOtherParticipant = endpointsCount !== 0
    const isFullscreenMode = sizeVideo === VIDEO_SIZE.BIG
    const isFloatingOutbound =
        isMobile && !screenShareEnabled
            ? isOtherParticipant
            : screenShareEnabled
              ? false
              : isPsychotherapyMode
                ? true
                : isFullscreenMode
                  ? isOtherParticipant
                  : false

    const isFloatingInbound = isOtherParticipant && screenShareEnabled
    const isSideBySideVideo = isOtherParticipant && !isFloatingOutbound && !isFloatingInbound
    const isFloatingWindowOffset = doNotCloseBannerEnabled && isMobile

    return { isFloatingOutbound, isFloatingInbound, isSideBySideVideo, isFloatingWindowOffset }
}

export function RoomPage({
    voximplantUserName,
    bookingHash,
    owner,
    outbound,
    inbound,
    hangUpLink,
    generateTokenApiUrl,
    sessionStartDateTime,
    sessionDuration,
    lobbyLink,
}: Props) {
    const remainSessionTime = calcSessionRemainingTime(sessionStartDateTime, sessionDuration)
    const [
        {
            endpoints,
            endpointsCount,
            endpointIds,
            call,
            navigateTo,
            error,
            isMyConnectionBad,
            isPeerConnectionBad,
        },
        { join, leave },
    ] = useVideoCall({
        bookingHash,
        voximplantUserName,
    })

    const [isPsychotherapyMode, setIsPsychotherapyMode] = useToggle(false)

    const [{ cameraEnabled }] = useInputDevicesContext()

    const [state, { init }] = useInitSdk()

    const onRender = useCallback<RefCallback<HTMLDivElement>>((instance) => {
        if (instance) {
            void init(instance, voximplantUserName, generateTokenApiUrl, owner, bookingHash)
        }
    }, [])
    const [screenShareEnabled, setScreenShareEnabled] = useToggle(false)
    const [sizeVideo, setSizeVideo] = useState<VIDEO_SIZE>(VIDEO_SIZE.BIG)
    const [doNotCloseBannerEnabled, toggleDoNotCloseBannerEnabled] = useToggle(false)
    const [isOpenedHelpModal, toggleIsOpenedHelpModal] = useToggle(false)
    const isMobile = useMediaIsMobile()
    const isIdle = useIdle(3 * 1000)

    useEffect(() => {
        if (navigateTo === undefined) return
        switch (navigateTo) {
            case PageType.Lobby:
                document.location.replace(lobbyLink)
                break
            case PageType.HangUp:
                document.location.replace(hangUpLink)
                break
        }
    }, [navigateTo])

    useMount(() => {
        getRootNode()?.classList.add(layoutClass)
    })

    useUnmount(() => {
        void leave()
        getRootNode()?.classList.remove(layoutClass, idleClass)
    })

    useEffect(() => {
        const root = getRootNode()
        isIdle ? root?.classList.remove(idleClass) : root?.classList.add(idleClass)
    }, [isIdle])

    useEffect(() => {
        if (state.ready && !state.error) {
            void join()
        }
    }, [state.ready])

    useHandleError(error)

    useBeforeUnload(!!call, "Вы действительно хотите завершить звонок?")

    useTimeoutFn(
        () => {
            toggleDoNotCloseBannerEnabled(owner === OWNER.Client)
        },
        remainSessionTime && remainSessionTime > doNotCloseBannerAppearBeforeEndTime
            ? remainSessionTime - doNotCloseBannerAppearBeforeEndTime
            : 0
    )

    const { isFloatingOutbound, isFloatingInbound, isSideBySideVideo, isFloatingWindowOffset } =
        calculateLayoutFlags({
            endpointsCount,
            screenShareEnabled,
            isPsychotherapyMode,
            isMobile,
            sizeVideo,
            doNotCloseBannerEnabled,
        })

    const myVideoVisible = cameraEnabled && !screenShareEnabled && !isPsychotherapyMode

    return (
        <>
            {!call && <Spinner fullscreen />}
            <InCallBanner
                visible={doNotCloseBannerEnabled}
                onVisibilityChange={toggleDoNotCloseBannerEnabled}
            >
                <div>Не&nbsp;закрывайте браузер после сессии.</div>
                <div>Мы&nbsp;предложим вам короткий опрос.</div>
            </InCallBanner>

            <ConnectionStatus
                bookingHash={bookingHash}
                isMyConnectionBad={isMyConnectionBad}
                isPeerConnectionBad={isPeerConnectionBad}
                toggleHelpModal={toggleIsOpenedHelpModal}
            />

            <div
                className={cn(
                    "is-flex-grow-1 is-fluid is-flex is-align-items-center room-call-page",
                    {
                        "room-call-page__side-by-side": isSideBySideVideo,
                    }
                )}
            >
                <RoomPageFrame
                    isFloating={isFloatingOutbound}
                    isWithBanner={isFloatingOutbound && isFloatingWindowOffset}
                    className={cn({
                        "is-hidden": !call,
                    })}
                    isRoundCorners={!isFloatingInbound || isFloatingOutbound}
                >
                    <div className={"video-container-wrapper"}>
                        <VideoUserNameWithPopover
                            key={`lets-force-popover-reflow-on-change-layout-${
                                call ? "force" : "none"
                            }-${sizeVideo}-${myVideoVisible.toString()}-${endpointsCount}-${doNotCloseBannerEnabled.toString()}`}
                            className={cn({
                                "is-my-video": endpointsCount > 0,
                                "is-video-hidden": isPsychotherapyMode,
                            })}
                            isIconOnly={!myVideoVisible}
                        >
                            <Text isSecondary className={cn({ "is-hidden": isPsychotherapyMode })}>
                                {outbound.name} (Вы)
                            </Text>
                        </VideoUserNameWithPopover>
                        <div
                            ref={onRender}
                            id="local-video-container"
                            className={cn("video-container", {
                                "is-contrast-bg-mobile has-speaker-border is-outbound":
                                    endpointsCount > 0 &&
                                    !(isPsychotherapyMode && screenShareEnabled),
                                "is-fullscreen-mobile": endpointsCount == 0,
                                "video-container__mirror": !screenShareEnabled,
                                "video-container__round-corners":
                                    isSideBySideVideo || isFloatingOutbound,
                                "video-container__hidden":
                                    isPsychotherapyMode && !screenShareEnabled,
                                "video-container__video-hidden": !myVideoVisible,
                            })}
                        />
                        {outbound.renderAvatar(
                            endpointsCount > 0,
                            myVideoVisible || isPsychotherapyMode || screenShareEnabled
                        )}
                    </div>
                </RoomPageFrame>
                {endpointsCount > 0 && (
                    <RoomPageFrame
                        autoGrid={endpointsCount > 1}
                        isFloating={isFloatingInbound}
                        isRoundCorners={isFloatingInbound || !isFloatingOutbound}
                        isWithBanner={isFloatingInbound && isFloatingWindowOffset}
                    >
                        {endpointIds.map((key) => {
                            console.log("endpoints[key]", endpoints[key])
                            return (
                                <div className="video-container-wrapper" key={key}>
                                    <VoxMediaPlayer
                                        mediaRenderer={endpoints[key]}
                                        className={cn("has-speaker-border", {
                                            "is-inbound":
                                                !(
                                                    endpoints[key]?.stream?.active &&
                                                    (endpoints[key]?.kind ===
                                                        VoximplantSdk.MediaRendererKind.Video ||
                                                        endpoints[key]?.kind ===
                                                            VoximplantSdk.MediaRendererKind.Sharing)
                                                ) && screenShareEnabled,
                                            "video-container__mirror":
                                                endpoints[key]?.kind !==
                                                VoximplantSdk.MediaRendererKind.Sharing,
                                            "video-container__round-corners":
                                                isSideBySideVideo || isFloatingInbound,
                                        })}
                                        avatar={inbound?.renderAvatar(
                                            false,
                                            endpoints[key]?.stream?.active &&
                                                (endpoints[key]?.kind ===
                                                    VoximplantSdk.MediaRendererKind.Video ||
                                                    endpoints[key]?.kind ===
                                                        VoximplantSdk.MediaRendererKind.Sharing)
                                        )}
                                        name={inbound?.name}
                                    />
                                </div>
                            )
                        })}
                    </RoomPageFrame>
                )}
            </div>
            <VideoControls
                classNames="is-fixed"
                call={call}
                isOpenedHelpModal={isOpenedHelpModal}
                endpoints={endpoints}
                isPsychotherapyMode={isPsychotherapyMode}
                hangUpLink={hangUpLink}
                lobbyLink={lobbyLink}
                onScreenShareEnabled={setScreenShareEnabled}
                setIsPsychotherapyMode={setIsPsychotherapyMode}
                toggleIsOpenedHelpModal={toggleIsOpenedHelpModal}
            />
            {!screenShareEnabled && (
                <ChangeVideoToggle
                    isPsychotherapyMode={isPsychotherapyMode}
                    setIsPsychotherapyMode={setIsPsychotherapyMode}
                    sizeVideo={sizeVideo}
                    onClickSizeVideo={setSizeVideo}
                />
            )}
        </>
    )
}
